export interface TaxonomyTranslationMatchesMetrics {
  translations: number
  matches: number
  durationInSeconds: number
}

export interface TaxonomyTranslationReviewProgress {
  noMatch: number
  match: number
  confirmed: number
  reviewed: number
}

export const TaxonomyTranslationStatus = {
  InProgress: 'in_progress',
  Completed: 'completed',
  Failed: 'failed',
} as const

export type TaxonomyTranslationStatus =
  (typeof TaxonomyTranslationStatus)[keyof typeof TaxonomyTranslationStatus]

export interface TaxonomyTranslation {
  uuid: string
  name: string
  status: TaxonomyTranslationStatus
  statusLabel: string
  createdBy: string
  createdByName: string
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  inputs: TaxonomyTranslationInput[]
  matchesMetrics: TaxonomyTranslationMatchesMetrics
  reviewProgress: TaxonomyTranslationReviewProgress
}

// Input type

export const TaxonomyTranslationInputType = {
  ReferenceTrafficking: 'reference_trafficking',
  ReferenceListValues: 'reference_list_values',
  CandidateTrafficking: 'candidate_trafficking',
  CandidateListValues: 'candidate_list_values',
} as const

export const TaxonomyTranslationInputSource = {
  File: 'file',
  Templates: 'templates',
} as const

export type TaxonomyTranslationInputSource =
  (typeof TaxonomyTranslationInputSource)[keyof typeof TaxonomyTranslationInputSource]
export type TaxonomyTranslationInputType =
  (typeof TaxonomyTranslationInputType)[keyof typeof TaxonomyTranslationInputType]

export interface TaxonomyTranslationInput {
  name: string
  type: TaxonomyTranslationInputType
  source: TaxonomyTranslationInputSource
}

// Translation field

export const TranslationStatus = {
  Reviewed: 'reviewed',
  Match: 'match',
  NoMatch: 'no_match',
  Excluded: 'excluded',
} as const

export type TranslationStatus = (typeof TranslationStatus)[keyof typeof TranslationStatus]

export interface Translation {
  status: TranslationStatus
  candidate: string | null
  reference: string | null
  translated: string | null
  confidence: number
  matchDescription: string | null
}

export const FieldType = {
  Text: 'text',
  List: 'list',
} as const

export type FieldType = (typeof FieldType)[keyof typeof FieldType]

export type ListValues = string[][]

export interface FieldTranslationInterface<T extends FieldType> extends Translation {
  id: string
  type: T
  channel: string | null
}

export type TextFieldTranslation = FieldTranslationInterface<typeof FieldType.Text>
export interface ListFieldTranslation extends FieldTranslationInterface<typeof FieldType.List> {
  listValueTranslations: ListValueTranslation[]
}

export interface ListValueTranslation extends Translation {
  index: number
  candidateAbbrev: string | null
  referenceAbbrev: string | null
}

export type FieldTranslation = TextFieldTranslation | ListFieldTranslation

export type TranslationKeys = keyof FieldTranslation

export interface FieldInterface<T extends FieldType> {
  name: string
  type: T
  channel: string | null
}
export type TextField = FieldInterface<typeof FieldType.Text>
export interface ListField extends FieldInterface<typeof FieldType.List> {
  listValues: ListValues
}

export type Field = TextField | ListField
